import { Injectable, DestroyRef, inject } from '@angular/core';
import { BehaviorSubject, fromEvent, merge } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

/**
 The purpose of this service is to determine whether a tab is actively being focused on. Clients seem to be using our platform in multiple browser tabs. While this isn't supported it is useful to know which tab they have open in order to direct downloads to only one tab.
*/
@Injectable({
  providedIn: 'root',
})
export class ActiveBrowserTabService {
  private _destroyRef = inject(DestroyRef);
  private _isTabActiveSubject = new BehaviorSubject<boolean>(
    this.isDocumentVisible(),
  );

  // Observable that other components can subscribe to
  public isTabActive$ = this._isTabActiveSubject.asObservable();

  get isTabActive(): boolean {
    return this._isTabActiveSubject.value;
  }

  constructor() {
    // Combine visibility change and focus/blur events
    merge(
      fromEvent(document, 'visibilitychange'),
      fromEvent(window, 'focus'),
      fromEvent(window, 'blur'),
    )
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => {
        this._isTabActiveSubject.next(
          this.isDocumentVisible() && document.hasFocus(),
        );
      });
  }

  // Helper method to check if document is visible
  private isDocumentVisible(): boolean {
    return document.visibilityState === 'visible';
  }
}
